import { AfterViewInit, Component, EventEmitter, OnInit } from '@angular/core';
import { ECharts, EChartsOption } from 'echarts';
import { IDateRangePickerDashboardItem } from 'src/app/components/date-picker/date-picker.component';
import { CommonLegendService } from 'src/app/services/common-legend.service';
import { IDashboardItemDataLoaderLink } from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import { DatasourceColumnId } from 'src/app/utils/datasource-sql-map/datasource';
import { EChartsAxisHelper } from 'src/app/utils/echarts/echarts-axis-helper';
import { EChartsTooltipHelper } from 'src/app/utils/echarts/echarts-tooltip-helper';
import { defaultColorGetterBuilder } from 'src/app/utils/echarts/series-color';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { datePartDateTrunc, getAllDatePartDateTrunc } from 'src/app/utils/sql';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { ISingleSelectSettingDashboardItem } from '../common/items/setting/single-select/single-select.component';
import { IToggleSettingDashboardItem } from '../common/items/setting/toggle/toggle.component';
import { StandardDashboardWorker } from '../common/standard-dashboard';

import { CompanyBillingChartDocs } from './company-billing.chart-documentation';
import { GcpBillingDataLoaderFactory } from './data-loaders/gcp-billing';
import { DashboardGcpBillingSqlMap } from './sql-maps/dashboard-gcp-billing-sql-map';
import { IDataLoaderResult } from 'src/app/services/data-loader/data-loader-result';

@Component({
    selector: 'app-company-billing',
    templateUrl: './company-billing.component.html',
    styleUrls: ['./company-billing.component.scss'],
    providers: [DashboardService, QueryContextService, CommonLegendService],
})
export class CompanyBillingComponent
    extends StandardDashboardWorker
    implements OnInit, AfterViewInit
{
    override DASHBOARD_NAME = 'Billing';
    override DASHBOARD_ID = 'company-billing';
    values = Object.values;

    private gcpProjectId: GcpProjectId = 'am-bi-prod';

    /////////////////////////////////////////////////////////////////
    ////
    ////    DATALOADERS
    ////    Some DataLoader can be used in multiple charts.
    ////    That's why there are created here and later referenced
    ////
    /////////////////////////////////////////////////////////////////

    override filterDataLoaders = {
        'company.billing.filter.cloud_project': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'company.billing.filter.cloud_project',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'CLOUD_PROJECT',
            datasourceSqlMap: DashboardGcpBillingSqlMap.sqlMap,
            filterContext: true,
        }).create(),
        'company.billing.filter.cloud_service': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'company.billing.filter.cloud_service',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'CLOUD_SERVICE',
            datasourceSqlMap: DashboardGcpBillingSqlMap.sqlMap,
            filterContext: true,
        }).create(),
        'company.billing.filter.cloud_sku_summary': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'company.billing.filter.cloud_sku_summary',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'CLOUD_SKU_SUMMARY',
            datasourceSqlMap: DashboardGcpBillingSqlMap.sqlMap,
            filterContext: true,
        }).create(),
        'company.billing.filter.cloud_sku': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'company.billing.filter.cloud_sku',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'CLOUD_SKU',
            datasourceSqlMap: DashboardGcpBillingSqlMap.sqlMap,
            filterContext: true,
        }).create(),
        'company.billing.filter.business_project': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'company.billing.filter.business_project',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'BUSINESS_PROJECT',
            datasourceSqlMap: DashboardGcpBillingSqlMap.sqlMap,
            filterContext: true,
        }).create(),
        'company.billing.filter.business_service': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'company.billing.filter.business_service',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'BUSINESS_SERVICE',
            datasourceSqlMap: DashboardGcpBillingSqlMap.sqlMap,
            filterContext: true,
        }).create(),
        'company.billing.filter.business_service_details_bijobs_job_id_project':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'company.billing.filter.business_service_details_bijobs_job_id_project',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId:
                    'BUSINESS_SERVICE_DETAILS_BIJOBS_JOB_ID_PROJECT',
                datasourceSqlMap: DashboardGcpBillingSqlMap.sqlMap,
                filterContext: true,
            }).create(),
        'company.billing.filter.business_service_details_bijobs_job_id_category':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'company.billing.filter.business_service_details_bijobs_job_id_category',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId:
                    'BUSINESS_SERVICE_DETAILS_BIJOBS_JOB_ID_CATEGORY',
                datasourceSqlMap: DashboardGcpBillingSqlMap.sqlMap,
                filterContext: true,
            }).create(),
        'company.billing.filter.business_service_details_bijobs_job_id_name':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'company.billing.filter.business_service_details_bijobs_job_id_name',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId:
                    'BUSINESS_SERVICE_DETAILS_BIJOBS_JOB_ID_NAME',
                datasourceSqlMap: DashboardGcpBillingSqlMap.sqlMap,
                filterContext: true,
            }).create(),
        'company.billing.filter.business_service_details_bijobs_orchestrator_options_type':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'company.billing.filter.business_service_details_bijobs_orchestrator_options_type',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId:
                    'BUSINESS_SERVICE_DETAILS_BIJOBS_ORCHESTRATOR_OPTIONS_TYPE',
                datasourceSqlMap: DashboardGcpBillingSqlMap.sqlMap,
                filterContext: true,
            }).create(),
        'company.billing.filter.business_service_details_bijobs_orchestrator_options_period':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'company.billing.filter.business_service_details_bijobs_orchestrator_options_period',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId:
                    'BUSINESS_SERVICE_DETAILS_BIJOBS_ORCHESTRATOR_OPTIONS_PERIOD',
                datasourceSqlMap: DashboardGcpBillingSqlMap.sqlMap,
                filterContext: true,
            }).create(),
        'company.billing.filter.cloud_service_details_bigquery_storage_dataset_ref_project_id':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'company.billing.filter.cloud_service_details_bigquery_storage_dataset_ref_project_id',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId:
                    'CLOUD_SERVICE_DETAILS_BIGQUERY_STORAGE_DATASET_REF_PROJECT_ID',
                datasourceSqlMap: DashboardGcpBillingSqlMap.sqlMap,
                filterContext: true,
            }).create(),
        'company.billing.filter.cloud_service_details_bigquery_storage_dataset_ref_dataset_id':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'company.billing.filter.cloud_service_details_bigquery_storage_dataset_ref_dataset_id',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId:
                    'CLOUD_SERVICE_DETAILS_BIGQUERY_STORAGE_DATASET_REF_DATASET_ID',
                datasourceSqlMap: DashboardGcpBillingSqlMap.sqlMap,
                filterContext: true,
            }).create(),
    };

    override chartDataLoaders = {
        'company.billing.gcp_billing': new GcpBillingDataLoaderFactory({
            createdInstanceId: 'company.billing.gcp_billing',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
        }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    [DASHBOARD ITEM <-> DATALOADER] LINKS
    ////
    /////////////////////////////////////////////////////////////////
    settingItemDateAggregation: ISingleSelectSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.DATE_AGGREGATION,
            viewtype: 'singleSelect',
            displayName: 'Date aggregation',
            value: 'day' as datePartDateTrunc,
            selectOptions: getAllDatePartDateTrunc(),
        },
    };

    settingItemCurrency: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.CURRENCY,
            viewtype: 'toggle',
            displayName: 'Currency',
            value: 'pln',
            selectOptions: ['pln', 'eur', 'usd'],
        },
    };

    // CHARTS
    gcp_cost: IDashboardItemDataLoaderLink = {
        id: 'company.billing.chart.gcp_cost',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'billing',
            createdInstanceId: 'company.billing.chart.gcp_cost',
            dashboardItem: {
                title: 'Google Cloud Platform Cost',
                subtitle: '',
                chartDocumentation: CompanyBillingChartDocs.gcp_cost,
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) => {
                const netGross =
                    context.settings.netGross == 'net' ? 'net_' : 'gross_';
                return `cost_${netGross}${context.settings.currency}`;
            },
            metricName: (context) => {
                const netGross =
                    context.settings.netGross == 'net' ? ' net' : ' gross';
                return `Cost ${netGross}`;
            },
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['company.billing.gcp_billing'].emitter,
    };

    // FILTERS
    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
    > = {
        ACTIVITY_DATE: {
            id: 'company.billing.filter.activity_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId: 'company.billing.filter.activity_date',
                datasourceColumnId: 'ACTIVITY_DATE',
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        CLOUD_PROJECT: {
            id: 'company.billing.filter.cloud_project',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'company.billing.filter.cloud_project',
                datasourceColumnId: 'CLOUD_PROJECT',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['company.billing.filter.cloud_project']
                    .emitter,
        },
        CLOUD_SERVICE: {
            id: 'company.billing.filter.cloud_service',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'company.billing.filter.cloud_service',
                datasourceColumnId: 'CLOUD_SERVICE',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['company.billing.filter.cloud_service']
                    .emitter,
        },
        CLOUD_SKU_SUMMARY: {
            id: 'company.billing.filter.cloud_sku_summary',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'company.billing.filter.cloud_sku_summary',
                datasourceColumnId: 'CLOUD_SKU_SUMMARY',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'company.billing.filter.cloud_sku_summary'
                ].emitter,
        },
        CLOUD_SKU: {
            id: 'company.billing.filter.cloud_sku',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'company.billing.filter.cloud_sku',
                datasourceColumnId: 'CLOUD_SKU',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['company.billing.filter.cloud_sku']
                    .emitter,
        },
        BUSINESS_PROJECT: {
            id: 'company.billing.filter.business_project',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'company.billing.filter.business_project',
                datasourceColumnId: 'BUSINESS_PROJECT',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'company.billing.filter.business_project'
                ].emitter,
        },
        BUSINESS_SERVICE: {
            id: 'company.billing.filter.business_service',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'company.billing.filter.business_service',
                datasourceColumnId: 'BUSINESS_SERVICE',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'company.billing.filter.business_service'
                ].emitter,
        },
        BUSINESS_SERVICE_DETAILS_BIJOBS_JOB_ID_PROJECT: {
            id: 'company.billing.filter.business_service_details_bijobs_job_id_project',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'company.billing.filter.business_service_details_bijobs_job_id_project',
                datasourceColumnId:
                    'BUSINESS_SERVICE_DETAILS_BIJOBS_JOB_ID_PROJECT',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'company.billing.filter.business_service_details_bijobs_job_id_project'
                ].emitter,
        },
        BUSINESS_SERVICE_DETAILS_BIJOBS_JOB_ID_CATEGORY: {
            id: 'company.billing.filter.business_service_details_bijobs_job_id_category',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'company.billing.filter.business_service_details_bijobs_job_id_category',
                datasourceColumnId:
                    'BUSINESS_SERVICE_DETAILS_BIJOBS_JOB_ID_CATEGORY',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'company.billing.filter.business_service_details_bijobs_job_id_category'
                ].emitter,
        },
        BUSINESS_SERVICE_DETAILS_BIJOBS_JOB_ID_NAME: {
            id: 'company.billing.filter.business_service_details_bijobs_job_id_name',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'company.billing.filter.business_service_details_bijobs_job_id_name',
                datasourceColumnId:
                    'BUSINESS_SERVICE_DETAILS_BIJOBS_JOB_ID_NAME',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'company.billing.filter.business_service_details_bijobs_job_id_name'
                ].emitter,
        },
        BUSINESS_SERVICE_DETAILS_BIJOBS_ORCHESTRATOR_OPTIONS_TYPE: {
            id: 'company.billing.filter.business_service_details_bijobs_orchestrator_options_type',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'company.billing.filter.business_service_details_bijobs_orchestrator_options_type',
                datasourceColumnId:
                    'BUSINESS_SERVICE_DETAILS_BIJOBS_ORCHESTRATOR_OPTIONS_TYPE',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'company.billing.filter.business_service_details_bijobs_orchestrator_options_type'
                ].emitter,
        },
        BUSINESS_SERVICE_DETAILS_BIJOBS_ORCHESTRATOR_OPTIONS_PERIOD: {
            id: 'company.billing.filter.business_service_details_bijobs_orchestrator_options_period',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'company.billing.filter.business_service_details_bijobs_orchestrator_options_period',
                datasourceColumnId:
                    'BUSINESS_SERVICE_DETAILS_BIJOBS_ORCHESTRATOR_OPTIONS_PERIOD',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'company.billing.filter.business_service_details_bijobs_orchestrator_options_period'
                ].emitter,
        },
        CLOUD_SERVICE_DETAILS_BIGQUERY_STORAGE_DATASET_REF_PROJECT_ID: {
            id: 'company.billing.filter.cloud_service_details_bigquery_storage_dataset_ref_project_id',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'company.billing.filter.cloud_service_details_bigquery_storage_dataset_ref_project_id',
                datasourceColumnId:
                    'CLOUD_SERVICE_DETAILS_BIGQUERY_STORAGE_DATASET_REF_PROJECT_ID',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'company.billing.filter.cloud_service_details_bigquery_storage_dataset_ref_project_id'
                ].emitter,
        },
        CLOUD_SERVICE_DETAILS_BIGQUERY_STORAGE_DATASET_REF_DATASET_ID: {
            id: 'company.billing.filter.cloud_service_details_bigquery_storage_dataset_ref_dataset_id',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'company.billing.filter.cloud_service_details_bigquery_storage_dataset_ref_dataset_id',
                datasourceColumnId:
                    'CLOUD_SERVICE_DETAILS_BIGQUERY_STORAGE_DATASET_REF_DATASET_ID',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'company.billing.filter.cloud_service_details_bigquery_storage_dataset_ref_dataset_id'
                ].emitter,
        },
    };

    override async ngOnInit() {
        super.ngOnInit();
        console.log({
            msg: 'BillingComponent.ngOnInit',
            cancellationToken: this.cancellationToken,
        });
    }

    async ngAfterViewInit() {
        console.log({
            msg: 'BillingComponent.ngAfterViewInit',
            cancellationToken: this.cancellationToken,
        });
        await this.standardDashboardInitialize();
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;
        initialContext.settings['netGross'] = 'gross';

        const activityDateIC: IDateRangePickerDashboardItem['datasourceColumn'] =
            {
                id: 'ACTIVITY_DATE',
                state: {
                    value: {
                        preset: 'last 30 days',
                        splitAggregate: 'day',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };

        // set initialContext for AppStore to Google Play
        initialContext.datasourceColumns = [
            // firebaseAppStoreIC,
            // firebaseCountryTierIC,
            activityDateIC,
        ];

        return initialContext;
    }
}
